import { Card, EditIcon, Grid, GridColumn, GridRow, MobilePhoneIcon } from '@bp/ui-components';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';
import { Page } from '../../../../components/Page/Page';
import { SubstitionAppSetupOverview } from '../../../../components/Substitutions/components/SubstitutionAppSetupOverview';
import { SubstitionAppAccessForm } from '../../../../components/Substitutions/forms/SubstitionAppAccessForm';
import { AppStatusResponse } from '../../../../components/Substitutions/forms/SubstitionAppSetupForm';
import { SubstitutionAppOverviewForm } from '../../../../components/Substitutions/forms/SubstitutionAppOverviewForm';
import { SubstitutionAppAccessModal } from '../../../../components/Substitutions/modals/SubstitutionAppAccessModal';
import { SubstitutionAppOverviewModal } from '../../../../components/Substitutions/modals/SubstitutionAppOverviewModal';
import { SubstitionAppSetupModal } from '../../../../components/Substitutions/modals/SubstitutionAppSetupModal';
import { SubstitutionAppUserModal } from '../../../../components/Substitutions/modals/SubstitutionAppUserModal';
import { usePermissionChecker } from '../../../../hooks/usePermissionChecker';
import { apiHelper } from '../../../../utils/apiHelper';

export type SubstitutionAppTimeValues = 'time' | 'lessonName';

export type SubstitutionAppFormType = {
  teacherPassword: string;
  studentPassword: string;
  allowIndividualLogins: boolean;
  useTeacherShortname: boolean;
  useClassShortname: boolean;
  useRoomShortname: boolean;
  timeDisplayMode: SubstitutionAppTimeValues;
  showClassGroups: boolean;
  showOriginalSubject: boolean;
  showCurrentSubject: boolean;
  showHolidays: boolean;
};

export const SubstitutionAppSettingsPage = () => {
  const { t } = useTranslation();
  const permissions = usePermissionChecker();

  const websiteLink = 'https://vertretungsplan.me/download/';

  const androidLink = 'https://play.google.com/store/apps/details?id=com.johan.vertretungsplan_2';

  const iosLink = 'https://itunes.apple.com/de/app/id1191463720?mt=8';

  const [setupModalOpen, setSetupModalOpen] = useState<boolean>(false);
  const [accessModalOpen, setAccessModalOpen] = useState<boolean>(false);
  const [appModalOpen, setAppModalOpen] = useState<boolean>(false);
  const [appStatus, setAppStatus] = useState<AppStatusResponse | undefined>(undefined);
  const [userModalOpen, setUserModalOpen] = useState<boolean>(false);

  useEffect(() => {
    apiHelper<AppStatusResponse>({
      path: '/app/status',
      method: 'GET',
      type: 'json',
    }).then((r) => {
      setAppStatus(r);
    });
  }, []);

  return (
    <Page isForbidden={!permissions?.canManageSubstitution()} title={t('substitutions.app.title')}>
      <Card
        wrapperClass='mb-6'
        showHeaderMargin
        header={{
          headline: t('substitutions.app.administration'),
          actions: [
            ...(permissions?.isOmniAdmin()
              ? [
                  {
                    callback: () => setSetupModalOpen(true),
                    icon: <MobilePhoneIcon />,
                    text: t('substitutions.app.editSetup'),
                  },
                  {
                    callback: () => setUserModalOpen(true),
                    icon: <EditIcon />,
                    text: t('substitutions.app.editMonitorUser'),
                  },
                ]
              : []),
            {
              callback: () => setAccessModalOpen(true),
              icon: <EditIcon />,
              disabled: !appStatus,
              text: t('substitutions.app.editAccess'),
            },
          ],
        }}
      >
        <Grid>
          <GridRow headline={t('common.overview')}>
            <SubstitionAppSetupOverview appStatus={appStatus} />
          </GridRow>
          <GridRow headline={t('common.access')}>
            <SubstitionAppAccessForm readonly />
          </GridRow>
        </Grid>
      </Card>

      <Card
        wrapperClass='mb-6'
        showHeaderMargin
        header={{
          headline: t('substitutions.app.output'),
          actions: [
            {
              callback: () => setAppModalOpen(true),
              icon: <EditIcon />,
              text: t('substitutions.app.editOutput'),
            },
          ],
        }}
      >
        <SubstitutionAppOverviewForm readonly />
      </Card>

      <Card
        wrapperClass='mb-6'
        header={{
          headline: t('substitutions.app.title', { context: 'short' }),
        }}
      >
        <GridRow breakpoint='laptop'>
          <GridColumn width={4}>
            <Card
              header={{
                headline: t('common.webpage'),
                actions: [
                  {
                    hierarchy: 'secondary',
                    text: t('common.toWebpage'),
                    callback: () => window.open(websiteLink, '_blank'),
                  },
                ],
              }}
            >
              <QRCode title='Website' size={128} value={'https://vertretungsplan.me/download/'} />
            </Card>
          </GridColumn>
          <GridColumn width={4}>
            <Card
              header={{
                headline: 'Android',
                actions: [
                  {
                    hierarchy: 'secondary',
                    text: t('common.toAppStore'),
                    callback: () => window.open(androidLink, '_blank'),
                  },
                ],
              }}
            >
              <QRCode
                title='Android'
                size={128}
                value={'https://play.google.com/store/apps/details?id=com.johan.vertretungsplan_2'}
              />
            </Card>
          </GridColumn>
          <GridColumn width={4}>
            <Card
              header={{
                headline: 'Apple',
                actions: [
                  {
                    hierarchy: 'secondary',
                    text: t('common.toAppStore'),
                    callback: () => window.open(iosLink, '_blank'),
                  },
                ],
              }}
            >
              <QRCode title='iOS iPhone/iPad' size={128} value={'https://itunes.apple.com/de/app/id1191463720?mt=8'} />
            </Card>
          </GridColumn>
        </GridRow>
      </Card>

      <SubstitionAppSetupModal isOpen={setupModalOpen} appStatus={appStatus} onClose={() => setSetupModalOpen(false)} />

      <SubstitutionAppAccessModal isOpen={accessModalOpen} onClose={() => setAccessModalOpen(false)} />

      <SubstitutionAppOverviewModal isOpen={appModalOpen} onClose={() => setAppModalOpen(false)} />

      <SubstitutionAppUserModal
        isOpen={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        monitorUser={appStatus?.monitorUser}
      />
    </Page>
  );
};
